import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { Row, Col } from "reactstrap";
import ChatIcon from '@mui/icons-material/Chat';
import Mic from "../../../assets/Icons/mic.svg";
import MicMute from "../../../assets/Icons/mic-mute.svg";
import Camera from "../../../assets/Icons/camera.svg";
import CameraOff from "../../../assets/Icons/camera-off.svg";
import Phone from "../../../assets/Icons/phone.svg";
import { useSocket } from "../../../store/context/SocketProvider";
import { Tabs } from "antd";
import { ToastContainer } from "react-toastify";
import Video from "../Video";
import VideoChat from "../VideoChat";
import UserStream from "./UserStream";
import Participants from "./Participants";

export default function VideoMeetingSession() {
  const {
    endMeeting,
    localStream,
    streams,
    muteStream,
    unmuteStream,
    turnOffVideo,
    turnOnVideo,
    isMuted,
    isVideoTrack,
    isGroupCall,
    isVideoCall
  } = useSocket();

  const videoMeeting = useSelector((state) => state.videoMeeting);
  const { me, meetId } = videoMeeting;
  const [showChat, setShowChat] = useState(false);

  const [activeStream, setActiveStream] = useState(null);
  const [activeUser, setActiveUser] = useState({});

  const maximizeUserVideo = (stream, user) => {
    console.log("video maximised", stream, user);
    // Extract the actual MediaStream from the stream object
    const mediaStream = stream?.current ? stream.current : stream;
    setActiveStream(mediaStream);
    setActiveUser(user);
  };

  useEffect(() => {
    console.log('streams:', streams);
    // Extract the actual MediaStream from localStream ref
    const mediaStream = localStream?.current ? localStream.current : localStream;
    setActiveStream(mediaStream);
    setActiveUser(me);
  }, [localStream, me, turnOnVideo]);

  const toggleChat = () => {
    var userChat = document.getElementsByClassName("user-chat");
    if (userChat) {
      userChat[0].classList.add("user-chat-show");
    }
    setShowChat((prev) => !prev);
  }

  const items = [
    {
      key: "1",
      label: "Chats",
      children: <VideoChat isShow={showChat} />,
    },
    {
      key: "2",
      label: "Participants",
      children: (
        <Participants
          streams={streams}
          me={me}
          localStream={localStream}
          isMuted={isMuted}
          isVideoTrack={isVideoTrack}
          isGroupCall={isGroupCall}
          isVideoCall={isVideoCall}
          muteStream={muteStream}
          unmuteStream={unmuteStream}
          turnOnVideo={turnOnVideo}
          turnOffVideo={turnOffVideo}
          maximizeUserVideo={maximizeUserVideo}
        />
      ),
    },
  ];

  return (
    <div className="vms-wrapper">
      <Row className="g-1 flex-nowrap vms-row">

        {/* ── Left: Video area ── */}
        <Col lg={8} className="col-xxl-8 col-md-8 col-12 p-0 vms-left-col">
          <div id="videomeeting-session" className="vms-session">

            {/* Meet ID */}
            <h3 id="meet-h1" className="vms-meet-id">{meetId}</h3>

            <div className="vms-inner">

              {/* ── Main active video ── */}
              <div className="bg-black text-white position-relative vms-main-video">

                {/* Active user info */}
                <div className="vms-user-info">
                  <div className="vms-user-info-inner">
                    {activeUser?.imageSrc && activeUser?.imageSrc !== "" ? (
                      <img
                        src={activeUser.imageSrc}
                        alt="User Avatar"
                        className="vms-avatar-img"
                      />
                    ) : (
                      <div className="bg-primary text-white vms-avatar-initials">
                        {activeUser?.contactName?.first?.charAt(0)?.toUpperCase()}
                      </div>
                    )}
                    <div className="text-light vms-user-name">
                      {activeUser?.contactName?.first} {activeUser?.contactName?.last}
                    </div>
                  </div>
                </div>

                {/* Controls */}
                <div id="controls" className="start-50 translate-middle-x">
                  <div className="bg-dark bg-opacity-50 vms-controls-bar">

                    {/* Mic */}
                    <div className="bg-primary vms-ctrl-btn">
                      {isMuted ? (
                        <img src={MicMute} alt="mic-mute" onClick={unmuteStream} />
                      ) : (
                        <img src={Mic} alt="mic" onClick={muteStream} />
                      )}
                    </div>

                    {/* Camera */}
                    {(!isGroupCall || (isGroupCall && isVideoCall)) && (
                      <div className="bg-primary vms-ctrl-btn">
                        {!isVideoTrack ? (
                          <img src={CameraOff} alt="camera-off" onClick={turnOnVideo} />
                        ) : (
                          <img src={Camera} alt="camera" onClick={turnOffVideo} />
                        )}
                      </div>
                    )}

                    {/* Chat toggle (mobile) */}
                    <div
                      className="bg-primary text-black d-md-none vms-ctrl-btn"
                      onClick={toggleChat}
                    >
                      <ChatIcon className="vms-ctrl-btn-icon" />
                    </div>

                    {/* End call */}
                    <div
                      className="bg-danger vms-ctrl-btn"
                      onClick={() => endMeeting("Meet179")}
                    >
                      <img src={Phone} alt="phone" />
                    </div>
                  </div>
                </div>

                {/* Active video / camera off */}
                {activeStream ? (
                  <Video
                    stream={activeStream}
                    id="my-video"
                    muted={true}
                    style={{ transform: "rotateY(180deg)", objectFit: "cover" }}
                  />
                ) : (
                  <div className="bg-dark vms-camera-off">
                    <h5 className="text-light">Camera is off</h5>
                  </div>
                )}
              </div>

              {/* ── User stream strip ── */}
              <div id="users-stream" className="vms-stream-strip">

                {/* Local stream — hidden when it is the active user */}
                {activeUser?._id !== me?._id && (
                  <UserStream
                    stream={localStream?.current ? localStream.current : localStream}
                    user={me}
                    mute={true}
                    maximizeUserVideo={maximizeUserVideo}
                  />
                )}

                {streams.length > 0 ? (
                  streams
                    .filter((obj) => obj.user._id !== activeUser?._id)
                    .map((obj) => (
                      <UserStream
                        key={obj.user._id}
                        stream={obj.stream?.current ? obj.stream.current : obj.stream}
                        user={obj.user}
                        mute={obj.mute}
                        maximizeUserVideo={maximizeUserVideo}
                      />
                    ))
                ) : (
                  <div className="border border-light bg-light shadow-sm vms-waiting">
                    <div className="text-primary vms-waiting-icon">
                      <i className="ri-video-chat-line" />
                    </div>
                    <div className="fw-semibold text-dark">Waiting for participants</div>
                    <div className="text-muted small">Share the call with others to start the meeting</div>
                  </div>
                )}
              </div>

            </div>
          </div>
        </Col>

        {/* ── Right: Chat / Participants ── */}
        <Col
          lg={4}
          className={`col-xxl-4 col-md-4 col-12 p-0 user-chat border-start vms-right-col ${showChat ? "user-chat-show" : ""}`}
        >
          <div className="w-100 position-relative">
            {isGroupCall ? (
              <div className="rounded-md">
                <Tabs type="card" items={items} defaultActiveKey="1" />
              </div>
            ) : (
              <Participants
                streams={streams}
                me={me}
                localStream={localStream}
                isMuted={isMuted}
                isVideoTrack={isVideoTrack}
                isGroupCall={isGroupCall}
                isVideoCall={isVideoCall}
                muteStream={muteStream}
                unmuteStream={unmuteStream}
                turnOnVideo={turnOnVideo}
                turnOffVideo={turnOffVideo}
                maximizeUserVideo={maximizeUserVideo}
              />
            )}
          </div>
        </Col>

      </Row>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}